import React, { useEffect } from 'react';
import { Redirect, Route, Switch, withRouter, useHistory } from 'react-router-dom';
import { useIntl } from 'react-intl';

import ToasterContainer from 'components/organisms/ToasterContainer';
import asyncComponent from 'components/templates/AsyncComponent';
import useIdleLogout from 'hooks/useIdleLogout';
import { permissionList } from 'hooks/usePermission';
import { store } from 'MainApp';

export const RestrictedRoute = ({ component: Component, authUser, title, ...rest }) => {
  useEffect(() => {
    if (title) {
      document.title = `Cripten Flash - ${title}`;
    }
  }, [title]);

  return (
    <Route
      {...rest}
      render={(props) =>
        authUser ? (
          <Component {...props} />
        ) : (
          <Redirect
            to={{
              pathname: '/signin',
              state: { from: props.location },
            }}
          />
        )
      }
    />
  );
};

const evaluatePermission = (permissions) => {
  const userPermissions = store.getState().auth.authUser?.permissions;
  return Array.isArray(permissions) && permissions.every((p) => userPermissions?.includes(p));
};

const checkDashboardVisibility = () => {
  const authUser = store.getState().auth.authUser;
  if (!authUser) return false;

  const isMerchant = authUser.type === 'merchant';
  const hasMenuDashboard = evaluatePermission([permissionList.menuDashboard]);

  if (isMerchant) {
    const merchantHasWidgets =
      evaluatePermission([permissionList.transactionRead]) || evaluatePermission([permissionList.walletRead]);
    return hasMenuDashboard && merchantHasWidgets;
  } else {
    const adminHasWidgets =
      evaluatePermission([permissionList.amountTransactionRead]) ||
      evaluatePermission([permissionList.profitRead]) ||
      evaluatePermission([permissionList.adjustmentFeeReportRead]) ||
      evaluatePermission([permissionList.bankfeeRead]) ||
      evaluatePermission([permissionList.summaryPendingWithdrawal]);
    return hasMenuDashboard && adminHasWidgets;
  }
};

const AsyncDashboard = asyncComponent(() =>
  checkDashboardVisibility() ? import('./Dashboard') : import('./Transactions/List')
);
const AsyncProfileSettings = asyncComponent(() => import('./ProfileSettings'));

const AsyncCustomerList = asyncComponent(() =>
  evaluatePermission([permissionList.customerRead]) ? import('./Users/List/Customer') : import('./RestrictedAccess')
);
const AsyncAdminList = asyncComponent(() =>
  evaluatePermission([permissionList.userRead]) ? import('./Users/List/Admin') : import('./RestrictedAccess')
);
const AsyncAdminDetails = asyncComponent(() =>
  evaluatePermission([permissionList.userRead]) ? import('./Users/Details/Admin') : import('./RestrictedAccess')
);
const AsyncMerchantList = asyncComponent(() =>
  evaluatePermission([permissionList.userMerchantRead]) ? import('./Users/List/Merchant') : import('./RestrictedAccess')
);
const AsyncOwnMerchantList = asyncComponent(() =>
  evaluatePermission([permissionList.userMerchantReadOwn])
    ? import('./Users/List/OwnMerchant')
    : import('./RestrictedAccess')
);
const AsyncCustomerBanksList = asyncComponent(() =>
  evaluatePermission([permissionList.customerRead, permissionList.customerBankRead])
    ? import('./Users/CustomerBanks/List')
    : import('./RestrictedAccess')
);
const AsyncMerchantUserDetails = asyncComponent(() =>
  evaluatePermission([permissionList.userMerchantRead])
    ? import('./Users/Details/Merchant')
    : import('./RestrictedAccess')
);
const AsyncCustomerDetails = asyncComponent(() =>
  evaluatePermission([permissionList.customerRead]) ? import('./Users/Details/Customer') : import('./RestrictedAccess')
);
const AsyncCreateAdminUser = asyncComponent(() =>
  evaluatePermission([permissionList.userCreate]) ? import('./Users/Create/AdminUser') : import('./RestrictedAccess')
);
const AsyncCreateMerchantUser = asyncComponent(() =>
  evaluatePermission([permissionList.userMerchantCreate])
    ? import('./Users/Create/MerchantUser')
    : import('./RestrictedAccess')
);
const AsyncCreateOwnMerchantUser = asyncComponent(() =>
  evaluatePermission([permissionList.userMerchantCreateOwn])
    ? import('./Users/Create/OwnMerchantUser')
    : import('./RestrictedAccess')
);
const AsyncCreateMerchant = asyncComponent(() =>
  evaluatePermission([permissionList.merchantCreate]) ? import('./Merchants/Create') : import('./RestrictedAccess')
);
const AsyncMerchantsList = asyncComponent(() =>
  evaluatePermission([permissionList.menuMerchants]) ? import('./Merchants/List') : import('./RestrictedAccess')
);
const AsyncMerchantsDetails = asyncComponent(() =>
  evaluatePermission([permissionList.merchantRead]) ? import('./Merchants/Details') : import('./RestrictedAccess')
);
const AsyncBanksManagement = asyncComponent(() =>
  evaluatePermission([permissionList.menuBanks]) ? import('./Banks/List') : import('./RestrictedAccess')
);
const AsyncBanksGlobal = asyncComponent(() =>
  evaluatePermission([permissionList.menuBanks]) ? import('./Banks/List/global') : import('./RestrictedAccess')
);
const AsyncBanksWeighting = asyncComponent(() =>
  evaluatePermission([permissionList.menuBanks])
    ? import('./Banks/List/merchantWeightings')
    : import('./RestrictedAccess')
);
const AsyncMonitor = asyncComponent(() =>
  evaluatePermission([permissionList.conversionMonitorRead])
    ? import('./Banks/List/monitor')
    : import('./RestrictedAccess')
);
const AsyncCbusList = asyncComponent(() =>
  evaluatePermission([permissionList.menuBanks, permissionList.bankCbuRead])
    ? import('./Banks/Cbus/List')
    : import('./RestrictedAccess')
);
const AsyncBanksDetails = asyncComponent(() =>
  evaluatePermission([permissionList.bankRead]) ? import('./Banks/Details') : import('./RestrictedAccess')
);
const AsyncCreateBank = asyncComponent(() =>
  evaluatePermission([permissionList.bankCreate]) ? import('./Banks/Create') : import('./RestrictedAccess')
);
const AsyncWalletMovements = asyncComponent(() =>
  evaluatePermission([permissionList.menuBanks, permissionList.bankWalletMovementRead])
    ? import('./Banks/WalletMovements')
    : import('./RestrictedAccess')
);
const AsyncCashoutList = asyncComponent(() =>
  evaluatePermission([permissionList.bankCashoutRead]) ? import('./Cashout/List') : import('./RestrictedAccess')
);
const AsyncCashoutCreate = asyncComponent(() =>
  evaluatePermission([permissionList.bankCashoutCreate]) ? import('./Cashout/Create') : import('./RestrictedAccess')
);
const AsyncCashoutDetails = asyncComponent(() =>
  evaluatePermission([permissionList.bankCashoutRead]) ? import('./Cashout/Details') : import('./RestrictedAccess')
);
const AsyncTransactionsList = asyncComponent(() =>
  evaluatePermission([permissionList.transactionRead]) ? import('./Transactions/List') : import('./RestrictedAccess')
);
const AsyncTransactionsErrors = asyncComponent(() =>
  evaluatePermission([permissionList.transactionErrorsRead])
    ? import('./Transactions/Errors')
    : import('./RestrictedAccess')
);
const AsyncExternalTransactionsList = asyncComponent(() =>
  evaluatePermission([permissionList.transactionExternalRead])
    ? import('./ExternalTransactions/List')
    : import('./RestrictedAccess')
);
const AsyncExternalTransactionsDetails = asyncComponent(() =>
  evaluatePermission([permissionList.transactionExternalRead])
    ? import('./ExternalTransactions/Details')
    : import('./RestrictedAccess')
);
const AsyncTransactionsAdjustment = asyncComponent(() =>
  evaluatePermission([permissionList.transactionsAdjustment])
    ? import('./TransactionsAdjustment')
    : import('./RestrictedAccess')
);
const AsyncTransactionsSimulate = asyncComponent(() =>
  evaluatePermission([permissionList.transactionsSimulate])
    ? import('./Transactions/Simulate')
    : import('./RestrictedAccess')
);
const AsyncSettlementTransactionsList = asyncComponent(() =>
  evaluatePermission([permissionList.settlementTransactionRead])
    ? import('./Settlement/Transactions/List')
    : import('./RestrictedAccess')
);
const AsyncSettlementTransactionsCreate = asyncComponent(() =>
  evaluatePermission([permissionList.settlementTransactionMerchantUpdate])
    ? import('./Settlement/Transactions/Create')
    : import('./RestrictedAccess')
);
const AsyncSettlementTransactionsDetails = asyncComponent(() =>
  evaluatePermission([permissionList.settlementTransactionRead])
    ? import('./Settlement/Transactions/Details')
    : import('./RestrictedAccess')
);
const AsyncIssues = asyncComponent(() =>
  evaluatePermission([permissionList.transactionIssueRead]) ? import('./Issues') : import('./RestrictedAccess')
);
const AsyncWalletBalanceHistory = asyncComponent(() =>
  evaluatePermission([permissionList.menuBalanceHistory])
    ? import('./Reports/WalletBalanceHistory')
    : import('./RestrictedAccess')
);
const AsyncTransactionsDetails = asyncComponent(() =>
  evaluatePermission([permissionList.transactionRead]) ? import('./Transactions/Details') : import('./RestrictedAccess')
);
const AsyncApprovalsTransactionsList = asyncComponent(() =>
  evaluatePermission([permissionList.customerRead, permissionList.transactionReviewRead])
    ? import('./Approvals/PendingTransactions/List')
    : import('./RestrictedAccess')
);
const AsyncApprovalsTransactionsDetails = asyncComponent(() =>
  evaluatePermission([permissionList.transactionReviewRead])
    ? import('./Approvals/PendingTransactions/Details')
    : import('./RestrictedAccess')
);
const AsyncApprovalsSofList = asyncComponent(() =>
  evaluatePermission([permissionList.customerDocumentRead])
    ? import('./Approvals/Sof/List')
    : import('./RestrictedAccess')
);
const AsyncApprovalsSofDetails = asyncComponent(() =>
  evaluatePermission([permissionList.customerRead, permissionList.customerDocumentRead])
    ? import('./Approvals/Sof/Details')
    : import('./RestrictedAccess')
);
const checkMerchantCredentialsVisibility = () => {
  const authUser = store.getState().auth.authUser;
  if (!authUser) return false;

  const isMerchant = authUser.type === 'merchant';
  const hasOAuthClientRead = evaluatePermission([permissionList.oAuthClientRead]);

  return isMerchant && hasOAuthClientRead;
};

const AsyncMerchantCredentials = asyncComponent(() =>
  checkMerchantCredentialsVisibility() ? import('./MerchantCredentials') : import('./RestrictedAccess')
);

const checkSettlementOwnVisibility = () => {
  const authUser = store.getState().auth.authUser;
  if (!authUser) return false;

  const isMerchant = authUser.type === 'merchant';
  const hasSettlementCreateOwn = evaluatePermission([permissionList.settlementCreateOwn]);

  return isMerchant && hasSettlementCreateOwn;
};

const AsyncSettlement = asyncComponent(() =>
  checkSettlementOwnVisibility() ? import('./Settlement') : import('./RestrictedAccess')
);
const AsyncBalances = asyncComponent(() =>
  evaluatePermission([permissionList.menuBalances, permissionList.merchantRead]) ||
  evaluatePermission([permissionList.bankRead])
    ? import('./Reports/Balances')
    : import('./RestrictedAccess')
);
const AsyncDailyReport = asyncComponent(() =>
  evaluatePermission([permissionList.summaryDailyTransactionsRead])
    ? import('./Reports/DailyReport')
    : import('./RestrictedAccess')
);
const AsyncAccountStatement = asyncComponent(() =>
  evaluatePermission([permissionList.merchantDailyReportsRead]) ||
  evaluatePermission([permissionList.merchantMonthlyReportsRead])
    ? import('./Reports/AccountStatement')
    : import('./RestrictedAccess')
);
const AsyncAuditList = asyncComponent(() =>
  evaluatePermission([permissionList.auditRead]) ? import('./Audits/List') : import('./RestrictedAccess')
);
const AsyncCustomerTransactions = asyncComponent(() =>
  evaluatePermission([permissionList.summaryTransactionsCustomerRead])
    ? import('./Reports/CustomerTransactionSummary')
    : import('./RestrictedAccess')
);
const AsyncNotificationsReport = asyncComponent(() =>
  evaluatePermission([permissionList.notificationRead])
    ? import('./Reports/NotificationsReport')
    : import('./RestrictedAccess')
);
const AsyncProviderRequestsReport = asyncComponent(() =>
  evaluatePermission([permissionList.providerRequestRead])
    ? import('./Reports/ProviderRequestsReport')
    : import('./RestrictedAccess')
);
const AsyncPixAndGoQrList = asyncComponent(() =>
  evaluatePermission([permissionList.pixAndGoRead]) ? import('./PixAndGo/QrList') : import('./RestrictedAccess')
);
const AsyncPixAndGoTransactions = asyncComponent(() =>
  evaluatePermission([permissionList.pixAndGoRead]) ? import('./PixAndGo/Transactions') : import('./RestrictedAccess')
);

const Routes = ({ match }) => {
  const intl = useIntl();
  const history = useHistory();
  const authUser = JSON.parse(localStorage.getItem('user_data'));
  const canViewDashboard = checkDashboardVisibility();

  useIdleLogout();

  useEffect(() => {
    const tokenListener = window.addEventListener('invalid_token', () => {
      localStorage.setItem('show_expired_session', true);
      history.push({
        pathname: '/signin',
        state: { from: { pathname: window.location.pathname } },
      });
    });
    return () => window.removeEventListener('invalid_token', tokenListener);
  }, [history]);

  return (
    <>
      <Switch>
        <RestrictedRoute
          exact
          path={`${match.url}/dashboard`}
          authUser={authUser}
          component={AsyncDashboard}
          title={intl.formatMessage({ id: 'page.title.dashboard' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/profile/:id`}
          authUser={authUser}
          component={AsyncProfileSettings}
          title={intl.formatMessage({ id: 'page.title.profile' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/users/customer`}
          authUser={authUser}
          component={AsyncCustomerList}
          title={intl.formatMessage({ id: 'page.title.users' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/users/administrator`}
          authUser={authUser}
          component={AsyncAdminList}
          title={intl.formatMessage({ id: 'page.title.users' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/users/administrator/:id`}
          authUser={authUser}
          component={AsyncAdminDetails}
          title={intl.formatMessage({ id: 'page.title.users' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/users/merchant`}
          authUser={authUser}
          component={AsyncMerchantList}
          title={intl.formatMessage({ id: 'page.title.users' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/users/own-merchant`}
          authUser={authUser}
          component={AsyncOwnMerchantList}
          title={intl.formatMessage({ id: 'page.title.users' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/users/banks`}
          authUser={authUser}
          component={AsyncCustomerBanksList}
          title={intl.formatMessage({ id: 'page.title.banksByUser' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/users/merchant/:id`}
          authUser={authUser}
          component={AsyncMerchantUserDetails}
          title={intl.formatMessage({ id: 'page.title.users' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/users/customer/:id`}
          authUser={authUser}
          component={AsyncCustomerDetails}
          title={intl.formatMessage({ id: 'page.title.customerDetails' })}
        />
        <RestrictedRoute
          path={`${match.url}/create/user/administrator`}
          authUser={authUser}
          component={AsyncCreateAdminUser}
          title={intl.formatMessage({ id: 'page.title.createUser' })}
        />
        <RestrictedRoute
          path={`${match.url}/create/user/merchant`}
          authUser={authUser}
          component={AsyncCreateMerchantUser}
          title={intl.formatMessage({ id: 'page.title.createUser' })}
        />
        <RestrictedRoute
          path={`${match.url}/create/user/own-merchant`}
          authUser={authUser}
          component={AsyncCreateOwnMerchantUser}
          title={intl.formatMessage({ id: 'page.title.createUser' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/create/merchant`}
          authUser={authUser}
          component={AsyncCreateMerchant}
          title={intl.formatMessage({ id: 'page.title.createMerchant' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/merchants`}
          authUser={authUser}
          component={AsyncMerchantsList}
          title={intl.formatMessage({ id: 'page.title.merchants' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/merchants/:id`}
          authUser={authUser}
          component={AsyncMerchantsDetails}
          title={intl.formatMessage({ id: 'page.title.merchantDetails' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/banks/management`}
          authUser={authUser}
          component={AsyncBanksManagement}
          title={intl.formatMessage({ id: 'page.title.banks' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/banks/global`}
          authUser={authUser}
          component={AsyncBanksGlobal}
          title={intl.formatMessage({ id: 'page.title.banks.global' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/banks`}
          authUser={authUser}
          component={AsyncBanksManagement}
          title={intl.formatMessage({ id: 'page.title.banks' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/banks/weighting`}
          authUser={authUser}
          component={AsyncBanksWeighting}
          title={intl.formatMessage({ id: 'page.title.banks.weighting' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/banks/monitor`}
          authUser={authUser}
          component={AsyncMonitor}
          title={intl.formatMessage({ id: 'page.title.monitor', defaultMessage: 'Monitor' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/create/bank`}
          authUser={authUser}
          component={AsyncCreateBank}
          title={intl.formatMessage({ id: 'page.title.banks' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/banks/wallet-movements`}
          authUser={authUser}
          component={AsyncWalletMovements}
          title={intl.formatMessage({ id: 'page.title.banks.walletMovements' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/banks/cbus`}
          authUser={authUser}
          component={AsyncCbusList}
          title={intl.formatMessage({ id: 'page.title.banks.cbus' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/banks/:id`}
          authUser={authUser}
          component={AsyncBanksDetails}
          title={intl.formatMessage({ id: 'page.title.banks' })}
        />

        <RestrictedRoute
          exact
          path={`${match.url}/cashout`}
          authUser={authUser}
          component={AsyncCashoutList}
          title={intl.formatMessage({ id: 'page.title.cashout' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/cashout/create`}
          authUser={authUser}
          component={AsyncCashoutCreate}
          title={intl.formatMessage({ id: 'page.title.cashout' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/cashout/:id`}
          authUser={authUser}
          component={AsyncCashoutDetails}
          title={intl.formatMessage({ id: 'page.title.cashout' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/transactions`}
          authUser={authUser}
          component={AsyncTransactionsList}
          title={intl.formatMessage({ id: 'page.title.transactions' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/transactions-errors`}
          authUser={authUser}
          component={AsyncTransactionsErrors}
          title={intl.formatMessage({ id: 'page.title.transactions' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/external-transactions`}
          authUser={authUser}
          component={AsyncExternalTransactionsList}
          title={intl.formatMessage({ id: 'page.title.externalTransactions' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/external-transactions/:id`}
          authUser={authUser}
          component={AsyncExternalTransactionsDetails}
          title={intl.formatMessage({ id: 'page.title.externalTransactions' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/transactions-adjustment`}
          authUser={authUser}
          component={AsyncTransactionsAdjustment}
          title={intl.formatMessage({ id: 'page.title.transactionsAdjustment' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/transactions-simulate`}
          authUser={authUser}
          component={AsyncTransactionsSimulate}
          title={intl.formatMessage({ id: 'page.title.transactionsSimulate' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/settlement/transactions/create`}
          authUser={authUser}
          component={AsyncSettlementTransactionsCreate}
          title={intl.formatMessage({ id: 'page.title.settlementTransactions' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/settlement/transactions/:id`}
          authUser={authUser}
          component={AsyncSettlementTransactionsDetails}
          title={intl.formatMessage({ id: 'page.title.settlementTransactions' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/settlement/transactions`}
          authUser={authUser}
          component={AsyncSettlementTransactionsList}
          title={intl.formatMessage({ id: 'page.title.settlementTransactions' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/issues`}
          authUser={authUser}
          component={AsyncIssues}
          title={intl.formatMessage({ id: 'page.title.issues' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/wallet-balance-history`}
          authUser={authUser}
          component={AsyncWalletBalanceHistory}
          title={intl.formatMessage({ id: 'page.title.reports' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/transactions/:id`}
          authUser={authUser}
          component={AsyncTransactionsDetails}
          title={intl.formatMessage({ id: 'page.title.transactionDetails' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/approvals/transactions`}
          authUser={authUser}
          component={AsyncApprovalsTransactionsList}
          title={intl.formatMessage({ id: 'page.title.pendingTransactions' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/approvals/transactions/:id`}
          authUser={authUser}
          component={AsyncApprovalsTransactionsDetails}
          title={intl.formatMessage({ id: 'page.title.transactionApproval' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/approvals/sof`}
          authUser={authUser}
          component={AsyncApprovalsSofList}
          title={intl.formatMessage({ id: 'page.title.sofApproval' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/approvals/sof/:id`}
          authUser={authUser}
          component={AsyncApprovalsSofDetails}
          title={intl.formatMessage({ id: 'page.title.sofApproval' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/merchant-credentials`}
          authUser={authUser}
          component={AsyncMerchantCredentials}
          title={intl.formatMessage({ id: 'page.title.credentials' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/settlement`}
          authUser={authUser}
          component={AsyncSettlement}
          title={intl.formatMessage({ id: 'page.title.settlement' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/balances`}
          authUser={authUser}
          component={AsyncBalances}
          title={intl.formatMessage({ id: 'page.title.reports' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/daily-report`}
          authUser={authUser}
          component={AsyncDailyReport}
          title={intl.formatMessage({ id: 'page.title.reports' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/statement`}
          authUser={authUser}
          component={AsyncAccountStatement}
          title={intl.formatMessage({ id: 'page.title.reports' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/customer-transactions`}
          authUser={authUser}
          component={AsyncCustomerTransactions}
          title={intl.formatMessage({ id: 'page.title.reports' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/notifications-report`}
          authUser={authUser}
          component={AsyncNotificationsReport}
          title={intl.formatMessage({ id: 'page.title.reports' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/provider-requests-report`}
          authUser={authUser}
          component={AsyncProviderRequestsReport}
          title={intl.formatMessage({ id: 'page.title.reports' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/audits`}
          authUser={authUser}
          component={AsyncAuditList}
          title={intl.formatMessage({ id: 'page.title.audits' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/pix-and-go/qrs`}
          authUser={authUser}
          component={AsyncPixAndGoQrList}
          title={intl.formatMessage({ id: 'page.title.pixAndGoQrs' })}
        />
        <RestrictedRoute
          exact
          path={`${match.url}/pix-and-go/transactions`}
          authUser={authUser}
          component={AsyncPixAndGoTransactions}
          title={intl.formatMessage({ id: 'page.title.pixAndGoTransactions' })}
        />
        <Route path="*">
          <Redirect to={canViewDashboard ? `${match.url}/dashboard` : `${match.url}/transactions`} />
        </Route>
      </Switch>
      <ToasterContainer />
    </>
  );
};

export default withRouter(Routes);
